import type { Hexcode } from 'hephaestus/core';

const baseColors = {
  neutral800: '#0A0C13',
  neutral700: '#35373F',
  neutral600: '#6D7078',
  neutral500: '#9C9DA3',
  neutral400: '#C9CACF',
  neutral300: '#E9EAEC',
  neutral200: '#F6F7F8',
  neutral100: '#FFFFFF',
  primary700: '#0F3434',
  primary600: '#196666',
  primary500: '#268282',
  primary400: '#A8CDCD',
  primary300: '#D4E6E6',
  primary200: '#EFFBFB',
  negative700: '#420B0B',
  negative600: '#9A9DA5',
  negative500: '#DC2626',
  negative400: '#EE9393',
  negative300: '#F8D4D4',
  negative200: '#FEF2F2',
  warning700: '#492F03',
  warning600: '#AC6F08',
  warning500: '#F59E0B',
  warning400: '#FACF85',
  warning300: '#FDECCE',
  warning200: '#FFFBEB',
  positive700: '#073116',
  positive600: '#0D622C',
  positive500: '#12873D',
  positive400: '#8BD1A5',
  positive300: '#D0EDDB',
  positive200: '#F0FDF4',
  informative700: '#0B1E46',
  informative600: '#163B8D',
  informative500: '#2563EB',
  informative400: '#92B1F5',
  informative300: '#D3E0FB',
  informative200: '#EFF6FF',
  supportY800: '#534B2D',
  supportY100: '#FEF8CD',
  supportO800: '#7D4B1C',
  supportO100: '#FAF2EA',
  supportR800: '#831831',
  supportR100: '#FDF2F6',
  supportP800: '#611A75',
  supportP100: '#FBF4FF',
  supportV800: '#371D95',
  supportV100: '#F3F3FF',
  supportB800: '#1E4D8A',
  supportB100: '#EFF9FF',
  supportT800: '#145252',
  supportT100: '#EFFBF8',
  supportFocus: '#3388FF',

  primary: '#3A51C2',
  secondary: '#2B4763',
  selectedType: '#2CA58D',
  black: '#000000',
  noteTagLine: '#86939F',
  noteBackground: '#EAECEE',
  noteAuthor: '#282D32',
  neutral40: '#DCDDE0',
  neutral80: '#4B4E58',
  gray200: '#DBDFE3',
  gray300: '#F7F7F7',
  gray500: '#667380',
  gray800: '#111315',
  boxBorder: '#E4ECF4',
  background: '#F7F7F7',
  wizardTitle: '#233950',
  errorBar: '#FADEE0',
};

/**
 * @deprecated Use `useTheme()` and access colors via `theme.color` or `theme.palette` instead.
 */
export const legacyColors: any = {
  ...baseColors,
  white: baseColors.neutral100,
  primary: baseColors.primary500,
  negative: baseColors.negative500,
  warning: baseColors.warning500,
  positive: baseColors.positive500,
  neutral: baseColors.neutral500,
  focus: baseColors.supportFocus,
  informative: baseColors.informative500,
};

/**
 * @deprecated Use `useTheme()` and access colors via `theme.color` or `theme.palette` instead.
 */
export const colors = {
  primary: baseColors.primary,
  secondary: baseColors.secondary,
  success: baseColors.positive500,
  warning: baseColors.warning500,
  error: baseColors.negative500,
  neutral30: '#ECEDEE',
  neutral40: baseColors.neutral40,
  neutral80: baseColors.neutral80,
  neutral100: baseColors.neutral500,
  neutral600: '#9A9DA5',
  white: baseColors.neutral100,
  selectedType: baseColors.selectedType,
  black: baseColors.black,
  noteTagLine: baseColors.noteTagLine,
  noteBackground: baseColors.noteBackground,
  noteAuthor: baseColors.noteAuthor,
  gray100: '#EAECEE',
  gray200: baseColors.gray200,
  gray300: baseColors.gray300,
  gray500: baseColors.gray500,
  gray800: baseColors.gray800,
  boxBorder: baseColors.boxBorder,
  goalsProgressBar: '#3A6085',
  background: baseColors.background,
  wizardTitle: baseColors.wizardTitle,
  errorBar: baseColors.errorBar,
};

export const adjust = (color: string, amount: number) => {
  const adjustedColor = color
    .replace(/^#/, '')
    .replace(/../g, (rgb) =>
      `0${Math.min(255, Math.max(0, parseInt(rgb, 16) + amount)).toString(16)}`.slice(
        -2,
      ),
    );
  return `#${adjustedColor}`;
};

export const graphColors = [
  adjust(colors.primary, -40),
  adjust(colors.primary, -20),
  colors.primary,
  adjust(colors.primary, 20),
  adjust(colors.primary, 40),
  adjust(colors.secondary, 40),
  adjust(colors.secondary, 20),
  colors.secondary,
  adjust(colors.secondary, -20),
  adjust(colors.secondary, -40),
  adjust(colors.warning, -40),
  adjust(colors.warning, -20),
  colors.warning,
  adjust(colors.warning, 20),
  adjust(colors.warning, 40),
  adjust(colors.error, 40),
  adjust(colors.error, 20),
  colors.error,
  adjust(colors.error, -20),
  adjust(colors.error, -40),
];

export type ColorTokens = {
  primary?: Hexcode;
  primaryHover?: Hexcode;
  primaryPressed?: Hexcode;
  onPrimary?: Hexcode;
  primaryContainer?: Hexcode;
  primaryVariant?: Hexcode;
  primaryVariantHover?: Hexcode;
  primaryVariantPressed?: Hexcode;
  onPrimaryVariant?: Hexcode;
  negative?: Hexcode;
  negativeHover?: Hexcode;
  negativePressed?: Hexcode;
  onNegative?: Hexcode;
  negativeVariant?: Hexcode;
  negativeVariantHover?: Hexcode;
  negativeVariantPressed?: Hexcode;
  onNegativeVariant?: Hexcode;
  negativeOutline?: Hexcode;
  positive?: Hexcode;
  positiveHover?: Hexcode;
  positivePressed?: Hexcode;
  onPositive?: Hexcode;
  positiveVariant?: Hexcode;
  positiveVariantHover?: Hexcode;
  positiveVariantPressed?: Hexcode;
  onPositiveVariant?: Hexcode;
  warning?: Hexcode;
  warningHover?: Hexcode;
  warningPressed?: Hexcode;
  onWarning?: Hexcode;
  warningVariant?: Hexcode;
  warningVariantHover?: Hexcode;
  warningVariantPressed?: Hexcode;
  onWarningVariant?: Hexcode;
  neutralHover?: Hexcode;
  neutralPressed?: Hexcode;
  outline?: Hexcode;
  outlineVariant?: Hexcode;
  surface?: Hexcode;
  onSurface?: Hexcode;
  onSurfaceVariant?: Hexcode;
  surfaceContainer?: Hexcode;
  surfaceContainerVariant?: Hexcode;
  background?: Hexcode;
  onBackground?: Hexcode;
  disabled?: Hexcode;
  onDisabled?: Hexcode;
  navigation?: Hexcode;
  onNavigation?: Hexcode;
  supportOneVariant?: Hexcode;
  supportOneOnVariant?: Hexcode;
  supportOneNew?: Hexcode;
  supportOneNewVariant?: Hexcode;
  supportOneOnNewVariant?: Hexcode;
  supportTwo?: Hexcode;
  supportTwoVariant?: Hexcode;
  supportTwoOnVariant?: Hexcode;
  supportThree?: Hexcode;
  supportThreeVariant?: Hexcode;
  supportThreeOnVariant?: Hexcode;
  supportFour?: Hexcode;
  supportFourVariant?: Hexcode;
  supportFourOnVariant?: Hexcode;
  supportFive?: Hexcode;
  supportFiveVariant?: Hexcode;
  supportFiveOnVariant?: Hexcode;
  supportSix?: Hexcode;
  supportSixVariant?: Hexcode;
  supportSixOnVariant?: Hexcode;
};
